/**
 * Utilitários de Validação de Documentos Brasileiros
 * - CPF: Validação completa com dígitos verificadores
 * - CNPJ: Validação completa com dígitos verificadores
 * - CEP: Busca via ViaCEP API
 */

// ==================== CPF ====================

/**
 * Remove caracteres não numéricos do CPF
 */
export const cleanCPF = (cpf) => {
  return cpf?.replace(/\D/g, '') || '';
};

/**
 * Formata CPF para exibição (000.000.000-00)
 */
export const formatCPF = (cpf) => {
  const cleaned = cleanCPF(cpf);
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Valida CPF usando algoritmo oficial
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {boolean} true se válido
 */
export const validateCPF = (cpf) => {
  const cleaned = cleanCPF(cpf);
  
  // Deve ter 11 dígitos
  if (cleaned.length !== 11) return false;
  
  // Rejeita CPFs com todos os dígitos iguais
  if (/^(\d)\1{10}$/.test(cleaned)) return false;
  
  // Valida primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.charAt(9))) return false;
  
  // Valida segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.charAt(10))) return false;
  
  return true;
};

/**
 * Máscara de input para CPF
 */
export const maskCPF = (value) => {
  const cleaned = cleanCPF(value);
  const limited = cleaned.slice(0, 11);
  
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  if (limited.length <= 9) return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
};

// ==================== CNPJ ====================

/**
 * Remove caracteres não numéricos do CNPJ
 */
export const cleanCNPJ = (cnpj) => {
  return cnpj?.replace(/\D/g, '') || '';
};

/**
 * Formata CNPJ para exibição (00.000.000/0000-00)
 */
export const formatCNPJ = (cnpj) => {
  const cleaned = cleanCNPJ(cnpj);
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Valida CNPJ usando algoritmo oficial
 * @param {string} cnpj - CNPJ com ou sem formatação
 * @returns {boolean} true se válido
 */
export const validateCNPJ = (cnpj) => {
  const cleaned = cleanCNPJ(cnpj);
  
  // Deve ter 14 dígitos
  if (cleaned.length !== 14) return false;
  
  // Rejeita CNPJs com todos os dígitos iguais
  if (/^(\d)\1{13}$/.test(cleaned)) return false;
  
  // Valida primeiro dígito verificador
  let size = cleaned.length - 2;
  let numbers = cleaned.substring(0, size);
  const digits = cleaned.substring(size);
  let sum = 0;
  let pos = size - 7;
  
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  // Valida segundo dígito verificador
  size = size + 1;
  numbers = cleaned.substring(0, size);
  sum = 0;
  pos = size - 7;
  
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

/**
 * Máscara de input para CNPJ
 */
export const maskCNPJ = (value) => {
  const cleaned = cleanCNPJ(value);
  const limited = cleaned.slice(0, 14);
  
  if (limited.length <= 2) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  if (limited.length <= 8) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  if (limited.length <= 12) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`;
};

// ==================== CEP ====================

/**
 * Remove caracteres não numéricos do CEP
 */
export const cleanCEP = (cep) => {
  return cep?.replace(/\D/g, '') || '';
};

/**
 * Formata CEP para exibição (00000-000)
 */
export const formatCEP = (cep) => {
  const cleaned = cleanCEP(cep);
  if (cleaned.length !== 8) return cep;
  return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
};

/**
 * Máscara de input para CEP
 */
export const maskCEP = (value) => {
  const cleaned = cleanCEP(value);
  const limited = cleaned.slice(0, 8);
  
  if (limited.length <= 5) return limited;
  return `${limited.slice(0, 5)}-${limited.slice(5)}`;
};

/**
 * Busca endereço pelo CEP via ViaCEP API
 * @param {string} cep - CEP com ou sem formatação
 * @returns {Promise<Object|null>} Dados do endereço ou null se não encontrado
 */
export const fetchAddressByCEP = async (cep) => {
  const cleaned = cleanCEP(cep);
  
  if (cleaned.length !== 8) {
    return { error: 'CEP deve ter 8 dígitos' };
  }
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
    const data = await response.json();
    
    if (data.erro) {
      return { error: 'CEP não encontrado' };
    }
    
    return {
      logradouro: data.logradouro || '',
      complemento: data.complemento || '',
      bairro: data.bairro || '',
      cidade: data.localidade || '',
      estado: data.uf || '',
      ibge: data.ibge || '',
      success: true
    };
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return { error: 'Erro ao buscar CEP. Tente novamente.' };
  }
};

/**
 * Valida se o CEP tem formato correto (8 dígitos)
 */
export const validateCEP = (cep) => {
  const cleaned = cleanCEP(cep);
  return cleaned.length === 8;
};

// ==================== PHONE ====================

/**
 * Formata telefone brasileiro
 */
export const maskPhone = (value) => {
  const cleaned = value?.replace(/\D/g, '') || '';
  const limited = cleaned.slice(0, 11);
  
  if (limited.length <= 2) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  if (limited.length <= 10) return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
  return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
};

export default {
  // CPF
  cleanCPF,
  formatCPF,
  validateCPF,
  maskCPF,
  // CNPJ
  cleanCNPJ,
  formatCNPJ,
  validateCNPJ,
  maskCNPJ,
  // CEP
  cleanCEP,
  formatCEP,
  validateCEP,
  maskCEP,
  fetchAddressByCEP,
  // Phone
  maskPhone
};
